import { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { logError } from '@/lib/logger';

type CopyFeedback = 'idle' | 'copied' | 'error';

type TemporaryPasswordPanelProps = {
  password: string;
  helperText?: string;
  autoFocusCopyButton?: boolean;
  className?: string;
};

export function TemporaryPasswordPanel({
  password,
  helperText,
  autoFocusCopyButton = false,
  className,
}: TemporaryPasswordPanelProps) {
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback>('idle');
  const copyButtonRef = useRef<HTMLButtonElement | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setCopyFeedback('idle');
    if (autoFocusCopyButton) {
      setTimeout(() => copyButtonRef.current?.focus(), 0);
    }
    return () => {
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, [password, autoFocusCopyButton]);

  const scheduleResetFeedback = () => {
    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
    }
    resetTimeoutRef.current = window.setTimeout(() => {
      setCopyFeedback('idle');
      resetTimeoutRef.current = null;
    }, 2000);
  };

  const handleCopy = async () => {
    if (!password) return;
    if (!navigator?.clipboard) {
      setCopyFeedback('error');
      scheduleResetFeedback();
      return;
    }

    try {
      await navigator.clipboard.writeText(password);
      setCopyFeedback('copied');
    } catch (error) {
      logError('Impossibile copiare la password temporanea', error);
      setCopyFeedback('error');
    } finally {
      scheduleResetFeedback();
    }
  };

  return (
    <div className={className}>
      <Label htmlFor="temporary_password">Password temporanea</Label>
      <div className="mt-2 flex items-center gap-2">
        <Input id="temporary_password" value={password} readOnly className="font-mono" />
        <Button
          type="button"
          variant="outline"
          onClick={handleCopy}
          disabled={!password}
          ref={copyButtonRef}
        >
          {copyFeedback === 'copied'
            ? 'Copiata'
            : copyFeedback === 'error'
              ? 'Riprova'
              : 'Copia'}
        </Button>
      </div>
      {copyFeedback === 'copied' && (
        <p className="mt-1 text-xs text-emerald-600">Password copiata negli appunti.</p>
      )}
      {copyFeedback === 'error' && (
        <p className="mt-1 text-xs text-destructive">
          Impossibile copiare automaticamente, copia manualmente.
        </p>
      )}
      {helperText && <p className="mt-2 text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}

export default TemporaryPasswordPanel;
